import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Store, Search, Loader2, FileText, ShoppingCart, Package, ChevronLeft, ChevronRight, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import axios from 'axios';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const sadApi = () => {
  const token = localStorage.getItem('sad_token');
  return axios.create({ baseURL: `${API}/api`, headers: { Authorization: `Bearer ${token}` } });
};

const SuperAdminShops = () => {
  const [shops, setShops] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchShops = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      const res = await sadApi().get('/sad/shops', { params });
      setShops(res.data.shops);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchShops(); }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchShops();
  };

  const toggleShop = async (shopId, current) => {
    try {
      await sadApi().put(`/sad/shops/${shopId}/status?enabled=${!current}`);
      fetchShops();
    } catch (e) { console.error(e); }
  };

  const deleteShop = async (shopId, shopName) => {
    if (!window.confirm(`Delete "${shopName}" and ALL its data (products, sales, invoices)? This cannot be undone.`)) return;
    try {
      await sadApi().delete(`/sad/shops/${shopId}`);
      toast.success(`Shop "${shopName}" deleted`);
      fetchShops();
    } catch (e) { toast.error('Delete failed'); }
  };

  const totalPages = Math.ceil(total / 15);

  return (
    <SuperAdminLayout>
      <div className="space-y-5" data-testid="sad-shops-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Shops</h1>
            <p className="text-slate-400 text-sm">{total} registered shops</p>
          </div>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search shops..."
                className="pl-10 pr-4 py-2 bg-[#1e293b] border border-slate-600 text-white text-sm rounded-sm focus:border-amber-500 focus:outline-none w-64"
                data-testid="sad-shops-search"
              />
            </div>
            <button type="submit" className="bg-amber-500 text-white px-4 py-2 text-sm font-medium hover:bg-amber-600">Search</button>
          </form>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
        ) : (
          <div className="bg-[#1e293b] border border-slate-700 overflow-hidden">
            <table className="w-full text-sm" data-testid="sad-shops-table">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-800/50">
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Shop</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Owner</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Plan</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Products</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Invoices</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Sales</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {shops.map((shop, i) => (
                  <motion.tr
                    key={shop.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="border-b border-slate-700/50 hover:bg-slate-800/30"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-emerald-400" />
                        <div>
                          <p className="text-white font-medium">{shop.name}</p>
                          <p className="text-slate-500 text-xs">{shop.city || ''} {shop.nif ? `| NIF: ${shop.nif}` : ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-300">{shop.owner?.name || '-'}</p>
                      <p className="text-slate-500 text-xs">{shop.owner?.email || ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${
                        shop.owner?.plan === 'pos' ? 'bg-amber-500/15 text-amber-400' : 'bg-purple-500/15 text-purple-400'
                      }`}>
                        {shop.owner?.plan?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-300">
                      <div className="flex items-center justify-center gap-1">
                        <Package className="w-3.5 h-3.5 text-slate-500" />{shop.product_count}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-300">
                      <div className="flex items-center justify-center gap-1">
                        <FileText className="w-3.5 h-3.5 text-slate-500" />{shop.invoice_count}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-300">
                      <div className="flex items-center justify-center gap-1">
                        <ShoppingCart className="w-3.5 h-3.5 text-slate-500" />{shop.sale_count}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => toggleShop(shop.id, shop.is_active !== false)}
                          className="inline-flex items-center gap-1"
                          data-testid={`toggle-shop-${shop.id}`}
                        >
                          {shop.is_active !== false ? (
                            <ToggleRight className="w-6 h-6 text-emerald-400" />
                          ) : (
                            <ToggleLeft className="w-6 h-6 text-slate-500" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteShop(shop.id, shop.name)}
                          className="p-1 hover:bg-slate-700 rounded text-slate-500 hover:text-red-400"
                          title="Delete shop"
                          data-testid={`delete-shop-${shop.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
                {shops.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No shops found</td></tr>
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
                <span className="text-slate-500 text-xs">Page {page} of {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="p-1.5 bg-slate-800 text-slate-400 hover:text-white disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="p-1.5 bg-slate-800 text-slate-400 hover:text-white disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminShops;
